/* Each system's own mark, taken from the product that owns it — the app icon EIS ships in
 * its manifest, and the favicon NextPass ships in its theme. Both are drawn exactly as they
 * are: no plate behind them, no recolouring, nothing added. They are the two marks that
 * survive a light and a dark page on their own, which is why they are the ones used here;
 * the horizontal wordmarks of both products are light-on-dark artwork and would need a dark
 * block painted behind them.
 *
 * A system registered after this table was written falls back to its initials, so the picker
 * never renders a hole where a mark should be.
 */

import { cn } from '@nextleap-al/admin-ui';

import eisLogo from '../assets/eis-logo.png';
import nextpassLogo from '../assets/nextpass-logo.ico';

/** Canonical key -> the artwork and the product it belongs to. `unipass` is the older
 *  internal spelling of NextPass, and it is the one the corpus and telemetry still use. */
const LOGOS: Record<string, { src: string; name: string }> = {
  eis: { src: eisLogo, name: 'EPOKA Interactive System' },
  unipass: { src: nextpassLogo, name: 'NextPass' },
  nextpass: { src: nextpassLogo, name: 'NextPass' },
};

/** The initials a fallback mark shows: two letters at most, which is all that fits. */
function initials(label: string): string {
  const words = label.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

export function SystemLogo({
  system,
  label,
  size = 28,
  className,
}: {
  /** The system's key, in either spelling. */
  system: string;
  /** What the system is called, for the fallback mark and the accessible name. */
  label: string;
  size?: number;
  className?: string;
}) {
  const logo = LOGOS[(system ?? '').trim().toLowerCase()];

  // Only the fallback gets a background, because it is a word rather than a picture and a
  // word needs something to sit on.
  if (!logo) {
    return (
      <span
        className={cn(
          'inline-flex shrink-0 items-center justify-center rounded-[var(--radius-sm)]',
          'bg-[var(--bg-tertiary)] font-semibold text-[var(--text-secondary)]',
          className,
        )}
        style={{ height: size, width: size, fontSize: size * 0.42 }}
        role="img"
        aria-label={label}
      >
        {initials(label)}
      </span>
    );
  }

  return (
    <img
      src={logo.src}
      alt={`${logo.name} logo`}
      // The corner radius is the page's own, not a change to the artwork: EIS's icon is a
      // full-bleed square, and square corners next to rounded cards read as a stray sprite.
      className={cn('block shrink-0 rounded-[var(--radius-sm)] object-contain', className)}
      style={{ height: size, width: size }}
    />
  );
}
